import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { Observable } from 'rxjs';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideDownload, lucideInfo, lucideRotateCw, lucideTriangleAlert } from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmNativeSelectImports } from '@spartan-ng/helm/native-select';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import {
  AccountingService,
  Download,
  MonthlyStatement,
  downloadErrorMessage,
  saveDownload,
} from '../../../services/accounting.service';
import { formatCurrency } from '../../../utils/currency.utils';
import { berlinPeriod, periodLabel, shiftPeriod } from '../../../utils/period.utils';

/**
 * Einstellungen → Belege → Monatsabrechnung: the monthly statement for the tax adviser.
 *
 * Pick a month, see its sales, fees and payout amount at a glance, download the
 * statement as PDF (to file with the bank statement) or CSV (for Excel / the adviser).
 */
@Component({
  selector: 'app-accounting-statement',
  standalone: true,
  imports: [
    FormsModule,
    RouterLink,
    NgIcon,
    HlmAlertImports,
    HlmButtonImports,
    HlmCardImports,
    HlmFieldImports,
    HlmNativeSelectImports,
    HlmSpinnerImports,
    HlmTypographyImports,
  ],
  providers: [provideIcons({ lucideDownload, lucideInfo, lucideRotateCw, lucideTriangleAlert })],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
  templateUrl: './accounting-statement.component.html',
})
export class AccountingStatementComponent {
  private readonly accounting = inject(AccountingService);

  private readonly currentPeriod = berlinPeriod(new Date());
  /** The running month (preliminary) and the 24 before it; last month is preselected. */
  readonly periods = Array.from({ length: 25 }, (_, i) => shiftPeriod(this.currentPeriod, -i)).map(
    (p) => ({
      value: p,
      label: p === this.currentPeriod ? `${periodLabel(p)} (laufend)` : periodLabel(p),
    }),
  );
  readonly selectedPeriod = signal(shiftPeriod(this.currentPeriod, -1));
  /** The running month is a preview: shown, but no document to download (the API refuses). */
  readonly isRunningMonth = computed(() => this.selectedPeriod() === this.currentPeriod);

  readonly statement = signal<MonthlyStatement | null>(null);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  /** Which download is running, e.g. "statement-pdf"; disables its button. */
  readonly busy = signal<string | null>(null);
  readonly downloadError = signal<string | null>(null);

  constructor() {
    this.load();
  }

  euro(cents: number | null | undefined): string {
    return `${formatCurrency(cents ?? 0)} €`;
  }

  selectPeriod(period: string): void {
    this.selectedPeriod.set(period);
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.accounting.getMonthlyStatement(this.selectedPeriod()).subscribe({
      next: (statement) => {
        this.statement.set(statement);
        this.loading.set(false);
      },
      error: (err) => {
        this.statement.set(null);
        this.loading.set(false);
        this.error.set(err.error?.detail || 'Die Abrechnung konnte nicht geladen werden.');
      },
    });
  }

  downloadStatement(format: 'pdf' | 'csv'): void {
    const period = this.selectedPeriod();
    this.download(
      `statement-${format}`,
      () => this.accounting.downloadMonthlyStatement(period, format),
      `schenkwerk-abrechnung-${period}.${format}`,
    );
  }

  private download(key: string, request: () => Observable<Download>, fallbackName: string) {
    this.busy.set(key);
    this.downloadError.set(null);
    request().subscribe({
      next: (download) => {
        this.busy.set(null);
        saveDownload(download, fallbackName);
      },
      error: async (err) => {
        this.busy.set(null);
        this.downloadError.set(await downloadErrorMessage(err));
      },
    });
  }
}
